/**
 * Guide engine v3 overlay - the popup (#1025, epic #1020). Spec R1.6 / R7 / R15 / R17.
 *
 * A theme-native card placed by `placement` percentages (placement.js), decoupled
 * from the rings (no anchor / no tail). Height grows to fit and scrolls inside.
 * Top-right Exit is immediate (single tap, no confirm, R7) and does NOT mark the
 * guide completed (the runtime's exit()). The bottom row renders the step's manual
 * buttons in array order with the primary (forward) button last = bottom-right (R4);
 * an action step (no manual button) shows the inline `actionHint` cue instead.
 *
 * Motion (R15): fade + slight scale in, ~150ms; reduced motion = instant.
 * A11y (R17): labeled non-modal dialog; heading + body announced; focus moves into
 * the popup on show; the cue is an aria-live polite region; Esc is NOT bound to
 * Exit (no destructive action on an easy-to-hit key) - Exit is the explicit
 * top-right control only.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, IconButton, Paper, Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { IconX, IconVolume } from '@tabler/icons-react';
import { placementToStyle } from './placement';
import { manualLabel, DEFAULT_GUIDE_STRINGS } from './strings';
import { useResolvedReducedMotion } from './motion';

const GuidePopup = ({
  step,
  manualButtons = [],
  actionHint = null,
  onExit,
  onManual,
  onReplay = null,
  strings = DEFAULT_GUIDE_STRINGS,
  reducedMotion,
  zIndex,
}) => {
  const reduced = useResolvedReducedMotion(reducedMotion);
  const containerRef = useRef(null);
  const primaryRef = useRef(null);
  const [entered, setEntered] = useState(reduced);

  // Enter transition (R15): start scaled-down + transparent, then settle.
  useEffect(() => {
    if (reduced) { setEntered(true); return undefined; }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [reduced]);

  // Focus into the popup on show (R17): the primary button if present, else the
  // dialog container so keyboard + screen-reader users land in the new content.
  useEffect(() => {
    const el = primaryRef.current || containerRef.current;
    if (el) el.focus();
  }, []);

  const pos = placementToStyle(step.placement);
  const heading = step.heading || null;
  const hasButtons = manualButtons.length > 0;

  return (
    <Box
      data-testid="guide-popup-wrap"
      data-guide-popup="true"
      data-entered={entered ? 'true' : 'false'}
      data-reduced-motion={reduced ? 'true' : 'false'}
      data-zindex={zIndex}
      sx={{
        position: 'fixed',
        ...pos,
        zIndex,
        opacity: entered ? 1 : 0,
        transform: entered ? 'scale(1)' : 'scale(0.96)',
        transformOrigin: 'top left',
        transition: reduced
          ? 'none'
          : (theme) => `opacity ${theme.ds.motion.fast} ${theme.ds.motion.easeOut}, transform ${theme.ds.motion.fast} ${theme.ds.motion.easeOut}`,
      }}
    >
      <Paper
        ref={containerRef}
        role="dialog"
        aria-modal="false"
        aria-label={heading || strings.ariaDialog}
        tabIndex={-1}
        elevation={0}
        data-testid="guide-popup"
        sx={(theme) => ({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'inherit',
          bgcolor: theme.palette.surface.overlay,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${theme.ds.radius.container}px`,
          boxShadow: theme.palette.overlay.shadow,
          backgroundImage: 'none',
          outline: 'none',
        })}
      >
        {/* SR-only announcement of the new step (R17). */}
        <Box component="span" aria-live="polite" sx={visuallyHidden}>
          {heading ? `${heading}. ` : ''}{step.body}
        </Box>

        <IconButton
          onClick={onExit}
          aria-label={strings.ariaExit}
          data-testid="guide-exit"
          size="small"
          sx={(theme) => ({
            position: 'absolute',
            top: theme.ds.space[2],
            right: theme.ds.space[2],
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary },
          })}
        >
          <IconX size={18} />
        </IconButton>

        <Box sx={(theme) => ({
          p: `${theme.ds.space[8]}px ${theme.ds.space[10]}px ${theme.ds.space[5]}px`,
          pr: `${theme.ds.space[12]}px`,
          overflowY: 'auto',
        })}
        >
          {heading && (
            <Typography
              component="h2"
              sx={(theme) => ({
                fontSize: `${theme.ds.font.h1}px`,
                fontWeight: theme.ds.font.weightMedium,
                color: theme.palette.text.primary,
                mb: `${theme.ds.space[3]}px`,
              })}
            >
              {heading}
            </Typography>
          )}
          <Typography
            sx={(theme) => ({
              fontSize: `${theme.ds.font.row}px`,
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
            })}
          >
            {step.body}
          </Typography>
        </Box>

        <Box sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: `${theme.ds.space[5]}px`,
          p: `0 ${theme.ds.space[10]}px ${theme.ds.space[7]}px`,
        })}
        >
          {/* Replay-current-step narration (R9) - bottom-left, out of the
              forward-button flow. Rendered only when the host wires audio. */}
          {onReplay && (
            <IconButton
              onClick={onReplay}
              aria-label={strings.ariaReplay}
              data-testid="guide-replay"
              size="small"
              sx={(theme) => ({
                mr: hasButtons ? 'auto' : 0,
                color: theme.palette.text.secondary,
                '&:hover': { color: theme.palette.text.primary },
              })}
            >
              <IconVolume size={18} />
            </IconButton>
          )}
          {hasButtons ? (
            manualButtons.map((btn, i) => (
              <Button
                key={btn.text != null ? `${btn.text}-${i}` : `btn-${i}`}
                ref={btn.isPrimary ? primaryRef : null}
                onClick={() => onManual && onManual(btn.condition)}
                disableElevation
                variant={btn.isPrimary ? 'contained' : 'text'}
                data-testid={btn.isPrimary ? 'guide-btn-primary' : 'guide-btn-secondary'}
                sx={(theme) => ({
                  textTransform: 'none',
                  minHeight: 36,
                  borderRadius: `${theme.ds.radius.button}px`,
                  fontSize: `${theme.ds.font.bar}px`,
                  ...(btn.isPrimary
                    ? {
                      bgcolor: theme.palette.buttonPrimary.bg,
                      color: theme.palette.buttonPrimary.text,
                      '&:hover': { bgcolor: theme.palette.buttonPrimary.bg, filter: 'brightness(1.06)' },
                    }
                    : { color: theme.palette.interactive }),
                })}
              >
                {manualLabel(btn, strings)}
              </Button>
            ))
          ) : (
            // Text-only cue (#1043): the highlight ring owns the colored
            // "act here" signal; a fixed-color dot here mismatched it.
            <Box
              role="status"
              aria-live="polite"
              data-testid="guide-cue"
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                mr: 'auto',
                fontSize: `${theme.ds.font.bar}px`,
                fontStyle: 'italic',
                color: theme.palette.text.secondary,
              })}
            >
              {actionHint || strings.cueFallback}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default GuidePopup;
